import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = async (authUser) => {
    if (!authUser) { setUser(null); return; }

    let { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', authUser.id)
      .maybeSingle();

    // Profile missing — create one (handles OAuth sign-ins and pre-trigger signups)
    if (!profile) {
      const meta = authUser.user_metadata || {};
      const baseUsername = (meta.username || authUser.email?.split('@')[0] || 'reader')
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '_')
        .slice(0, 25);

      // Try username, then username_xxxx if taken
      let username = baseUsername;
      for (let attempt = 0; attempt < 5; attempt++) {
        const { data: created, error } = await supabase
          .from('profiles')
          .insert({
            id: authUser.id,
            username,
            display_name: meta.full_name || meta.display_name || meta.name || username,
            avatar_url: meta.avatar_url || meta.picture || '',
          })
          .select()
          .single();
        if (!error) { profile = created; break; }
        // Username taken — try with a random suffix
        if (error.code === '23505' && error.message.includes('username')) {
          username = `${baseUsername}_${Math.random().toString(36).slice(2, 6)}`;
        } else {
          break;
        }
      }
    }

    setUser(profile ? { 
      ...profile, 
      email: authUser.email,
      email_confirmed: authUser.email_confirmed_at != null 
    } : null);
  };

  useEffect(() => {
    // Restore existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      loadProfile(session?.user ?? null).finally(() => setLoading(false));
    });

    // Keep in sync with Supabase auth events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      loadProfile(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const register = async (username, display_name, email, password) => {
    const clean = username.toLowerCase().trim();

    // Check username availability
    const { data: taken } = await supabase.from('profiles').select('id').eq('username', clean).maybeSingle();
    if (taken) throw new Error('Username already taken');

    // Pass username + display_name as metadata — the DB trigger creates the profile row
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${siteUrl}/login`,
        data: { username: clean, display_name: display_name || clean },
      },
    });
    if (error) throw error;

    const authUser = data.user;
    if (!authUser) throw new Error('Signup failed — check your email to confirm your account');

    await loadProfile(authUser);
  };

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await loadProfile(data.user);
  };

  const loginWithGoogle = async () => {
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${siteUrl}/feed` },
    });
    if (error) throw error;
  };

  const sendPasswordReset = async (email) => {
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${siteUrl}/reset-password`,
    });
    if (error) throw error;
  };

  const updatePassword = async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
    await supabase.auth.signOut();
    setUser(null);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const updateProfile = async ({ username, display_name, bio, avatar_url }) => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) throw new Error('Not authenticated');

    const updates = {};
    if (username !== undefined) updates.username = username;
    if (display_name !== undefined) updates.display_name = display_name;
    if (bio !== undefined) updates.bio = bio;
    if (avatar_url !== undefined) updates.avatar_url = avatar_url;

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', authUser.id)
      .select()
      .single();
    if (error) throw error;

    setUser((prev) => ({ ...prev, ...data }));
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, updateProfile, sendPasswordReset, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
